import React, { Fragment } from 'react';
import styled from 'styled-components';
import Multilingual from '~~utils/Multilingual';
import Button from '~~elements/Button';
import { exportGameBoxScoreExcel } from '../service';
import moment from 'moment';

const StyledDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  label {
    margin: 0 1rem;
  }
  .score-board {
    display: flex;
    max-width: 80%;
    overflow-x: auto;
    text-align: center;
    padding: 0 0 1rem;
    .column {
      width: 2rem;
      border-right: 1px solid #eee;
      &:last-child {
        border-right: 0;
      }

      .cell {
        width: 100%;
        height: 1.6rem;
        display: block;
        border-bottom: 1px solid #eee;
        &.header {
          font-weight: 600;
        }
        &:last-child {
          border-bottom: 0;
        }
      }
    }
  }
  .basic-info {
    margin-bottom: 1rem;
    display: grid;
    grid-template-columns: 1fr 2fr;
    > * {
      border-bottom: 1px solid #eee;
      margin: 0;
      padding: 0.25rem 0.5rem;
      &.row-gap {
        margin-bottom: 1rem;
      }
    }
  }
`;

const getTeamName = (team) => {
  if (!team) {
    return Multilingual(`GAME.TEAM_NONE`);
  }

  return team;
};

const GameOverviewSection = ({ game, playerLoading }) => {
  const getFinishedGameInfo = () => {
    const finishedStatusFragment = (
      <Fragment>
        <h4>{Multilingual(`GAME.FINISHED_STATUS.WORDING`)}</h4>
        <label>{Multilingual(`GAME.FINISHED_STATUS.${game.info.finished_status}`)}</label>
      </Fragment>
    );

    if (game.info.finished_status === 'SUSPENDED') {
      return finishedStatusFragment;
    }

    let matchResultWording = `${game.away.runs}:${game.home.runs} `;
    switch (game.info.winner_side) {
      case 'AWAY':
        matchResultWording += `- ${game.away.team} ${Multilingual(`GAME.MATCH_RESULT.WIN`)}`;
        break;
      case 'HOME':
        matchResultWording += `- ${game.home.team} ${Multilingual(`GAME.MATCH_RESULT.WIN`)}`;
        break;
      case 'TIE':
        matchResultWording += Multilingual(`GAME.MATCH_RESULT.DRAW`);
        break;
      default:
        return finishedStatusFragment;
    }

    return (
      <Fragment>
        {finishedStatusFragment}
        <h4>{Multilingual(`GAME.MATCH_RESULT.WORDING`)}</h4>
        <label>{matchResultWording}</label>
      </Fragment>
    );
  };

  const getTeamScoreSection = () => {
    const awayTeamName = <label>{getTeamName(game.away.team)}</label>;
    const homeTeamName = <label>{getTeamName(game.home.team)}</label>;

    if (game.info.status !== 'FINISHED') {
      return (
        <h4>
          {awayTeamName}
          <label>-:-</label>
          {homeTeamName}
        </h4>
      );
    }

    return (
      <h4>
        {awayTeamName}
        <label>{`${game.away.runs}:${game.home.runs}`}</label>
        {homeTeamName}
      </h4>
    );
  };

  const getScoreBoardSection = () => {
    const scoreboardColumns = [
      ['R', '-', '-'],
      ['H', '-', '-'],
    ];
    if (game.info.status !== 'FINISHED') {
      scoreboardColumns.unshift(['-', '-', '-']);
    } else {
      scoreboardColumns[0][1] = game.away.runs;
      scoreboardColumns[0][2] = game.home.runs;
      scoreboardColumns[1][1] = game.away.hits;
      scoreboardColumns[1][2] = game.home.hits;
      if (game.info.runs_without_innings) {
        scoreboardColumns.unshift(['-', '-', '-']);
      } else {
        for (let inning = 0; inning < game.info.innings; inning++) {
          scoreboardColumns.splice(inning, 0, [
            (inning + 1).toString(),
            !game.away.scores[inning] ? '-' : game.away.scores[inning],
            !game.home.scores[inning] ? '-' : game.home.scores[inning],
          ]);
        }
      }
    }

    return (
      <div className="score-board">
        {scoreboardColumns.map((scoreboardColumn, i) => (
          <div key={i} className="column">
            <span className="cell header">{scoreboardColumn[0]}</span>
            <span className="cell">{scoreboardColumn[1]}</span>
            <span className="cell">{scoreboardColumn[2]}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <StyledDiv>
      {getTeamScoreSection()}
      {getScoreBoardSection()}
      <div className="basic-info">
        <h4>{Multilingual(`GAME.STATUS.WORDING`)}</h4>
        <label>{Multilingual(`GAME.STATUS.${game.info.status}`)}</label>
        {game.info.status === 'FINISHED' && getFinishedGameInfo()}
        <h4>{Multilingual(`GAME.SCHEDULED_START_AT`)}</h4>
        <label>
          {moment(game.info.time.scheduled_start_at).isValid()
            ? moment(game.info.time.scheduled_start_at).format('YYYY/MM/DD HH:mm')
            : '-'}
        </label>
        <h4>{Multilingual(`GAME.STARTED_AT`)}</h4>
        <label>
          {moment(game.info.time.started_at).isValid()
            ? moment(game.info.time.started_at).format('YYYY/MM/DD HH:mm')
            : '-'}
        </label>
        <h4 className="row-gap">{Multilingual(`GAME.ENDED_AT`)}</h4>
        <label className="row-gap">
          {moment(game.info.time.ended_at).isValid() ? moment(game.info.time.ended_at).format('YYYY/MM/DD HH:mm') : '-'}
        </label>
        <h4>{Multilingual(`GAME.LOCATION`)}</h4>
        <label>{game.info.location}</label>
        <h4>{Multilingual(`GAME.NEWS.WORDING`)}</h4>
        <label>{!game.news ? 'X' : 'O'}</label>
        <h4>{Multilingual(`GAME.MEDIA`)}</h4>
        <label>{!game.media || game.media.length === 0 ? 'X' : 'O'}</label>
        <h4>{Multilingual(`GAME.STATS_COMPLETENESS.WORDING`)}</h4>
        <label>{Multilingual(`GAME.STATS_COMPLETENESS.${game.info.stats_completeness}`)}</label>
      </div>
      {['BOX', 'PBP'].includes(game.info.stats_completeness) && (
        <Button width="unset" color="blue" onClick={() => exportGameBoxScoreExcel(game)}>
          {Multilingual(`GAME.STATS_COMPLETENESS.EXPORT_EXCEL`)}
        </Button>
      )}
    </StyledDiv>
  );
};

export default GameOverviewSection;
